import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { BrandMark, Icon, PageSectionHeader, resolveBrand } from "../../components/ui";
import type { IconName } from "../../components/ui/Icon";
import { useIntegrationsApi } from "./hooks/useIntegrationsApi";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { AddAccountModal } from "../../components/integrations/AddAccountModal";
import { SyncProgressBar } from "./SyncProgressBar";
import {
  accountBadge,
  itemCount,
  repairLabel,
  sourceCounterLine,
  surfaceBadge,
  surfaceProgress,
  syncErrorDetail,
} from "../../api/sourceStatus";
import { untilTime } from "../../utils/time";
import type { Account, SourceStatus, SurfaceSync, Tone } from "../../api/sourceStatus";

const SOURCE_META: Readonly<Record<string, { label: string; icon: IconName }>> = {
  google: { label: "Google", icon: "mail" },
  telegram: { label: "Telegram", icon: "send" },
  x: { label: "X", icon: "plug" },
  linkedin: { label: "LinkedIn", icon: "plug" },
};

const TONE_TEXT: Readonly<Record<Tone, string>> = {
  green: "text-green-400",
  blue: "text-blue-400",
  amber: "text-amber-400",
  red: "text-red-400",
  grey: "text-content-tertiary",
};

const TONE_DOT: Readonly<Record<Tone, string>> = {
  green: "bg-green-400",
  blue: "bg-blue-400",
  amber: "bg-amber-400",
  red: "bg-red-400",
  grey: "bg-content-tertiary",
};

/** Failed-surface detail line from the typed error (empty for none). */
function failedDetail(surface: SurfaceSync): string | null {
  if (surface.state !== "failed") return null;
  const detail = syncErrorDetail(surface.error);
  return detail.length > 0 ? detail : null;
}

// NOTE: the settled badge number comes from `itemCount(surface)` — the
// surface's REAL graph item count (§3 items) — never from SyncReport.ingested
// (an envelope counter for ONE run: cumulative for a bootstrap, 1 for a 30s
// catchup — the "Email · 1" incident).

/**
 * The accounts screen — a PURE render of `source.status.list` (§3 L4).
 * Every label, counter, and button is a function of the contract value;
 * nothing is derived from raw sync rows or integration flags.
 *
 * @tested-by: tst_fe_unit_accounts_020..027
 */
export function AccountsPanel(): JSX.Element {
  const { getSourceStatus, disconnectAccount } = useIntegrationsApi();
  const runtime = useAppRuntime();
  const [sources, setSources] = useState<readonly SourceStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  // When set, the modal re-authorizes this account's display identity
  // rather than adding a new account.
  const [reauthLabel, setReauthLabel] = useState<string | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  // Account whose per-surface detail is expanded (accordion; one at a time).
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const handleDisconnect = useCallback(
    (sourceId: string, account: Account): void => {
      void disconnectAccount(sourceId, account.account_id)
        .then(() => { setRefreshKey((k) => k + 1); })
        .catch(() => { /* surfaced on next refresh */ });
    },
    [disconnectAccount],
  );

  // Auto-refresh on sync status changes (INV-4)
  useEffect(() => {
    return runtime.transport.onEventType(
      ["sync.status_changed", "sync.progress", "source.account.connected"],
      () => { setRefreshKey((k) => k + 1); },
    );
  }, [runtime.transport]);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    void (async () => {
      try {
        const status = await getSourceStatus();
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!cancelled) {
          setSources(status.sources);
        }
      } catch {
        // ignore
      } finally {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!cancelled) setLoading(false);
      }
    })();
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const now = new Date();
  // ПРАВИЛО №1 оператора / plan H1 step 1: нет подключенных аккаунтов — нет
  // строчек. A registered source is a capability, not an account: only
  // sources with ≥1 REAL account render; connecting goes through "+".
  const connected = sources.filter((s) => s.accounts.length > 0);
  const totalAccounts = connected.reduce((sum, s) => sum + s.accounts.length, 0);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-2">
        <PageSectionHeader
          title="Accounts"
          description="Connect and manage your accounts."
          actions={(
            <button
              type="button"
              onClick={() => { setShowModal(true); }}
              className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-surface-hover"
            >
              <Icon name="plus" size={18} className="text-content-secondary" />
            </button>
          )}
        />

        <div className="h-5" />

        {loading ? (
          <div className="py-10 text-center text-content-tertiary text-sm">
            Loading...
          </div>
        ) : (
          <>
            {totalAccounts === 0 && (
              <p className="py-4 text-center text-content-tertiary text-sm">
                Connect a source to start syncing
              </p>
            )}
            <div className="space-y-4">
              {connected.map((source) => {
                const meta = SOURCE_META[source.source_id] ?? {
                  label: source.source_id,
                  icon: "plug" as IconName,
                };
                const brand = resolveBrand(source.source_id);
                return (
                  <div key={source.source_id}>
                    {/* Source group header: brand + counters from the ONE
                        rendered collection (§3). */}
                    <div className="flex items-center justify-between px-1 pb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {brand ? (
                          <BrandMark brand={brand} size={18} />
                        ) : (
                          <Icon name={meta.icon} size={14} className="text-content-secondary" />
                        )}
                        <span className="text-sm font-medium text-content">{meta.label}</span>
                        <span className="text-xs text-content-tertiary">
                          {sourceCounterLine(source)}
                        </span>
                      </div>
                    </div>

                    {(
                      <div className="rounded-xl bg-surface-tertiary border border-edge overflow-hidden divide-y divide-edge">
                        {source.accounts.map((account) => {
                          const badge = accountBadge(account, now);
                          const key = `${source.source_id}-${account.account_id}`;
                          const isOpen = expandedKey === key;
                          return (
                            <div key={key} className="w-full">
                              {/* Header — click toggles the per-surface detail */}
                              <div className="flex items-center justify-between px-4 py-3 hover:bg-surface-hover transition-colors">
                                <button
                                  type="button"
                                  onClick={() => { setExpandedKey(isOpen ? null : key); }}
                                  aria-expanded={isOpen}
                                  className="flex items-center gap-2.5 min-w-0 flex-1 text-left bg-transparent border-none p-0 cursor-pointer"
                                >
                                  <span
                                    className={`shrink-0 text-content-muted text-[10px] w-3 transition-transform ${isOpen ? "rotate-90" : ""}`}
                                  >
                                    ▶
                                  </span>
                                  <span className="text-sm font-medium text-content truncate">
                                    {account.display}
                                  </span>
                                </button>
                                <div className="flex items-center gap-2 shrink-0">
                                  <div className={`w-2 h-2 rounded-full ${TONE_DOT[badge.tone]}`} />
                                  <span className={`text-xs ${TONE_TEXT[badge.tone]}`}>
                                    {badge.label}
                                  </span>
                                </div>
                              </div>

                              {/* Per-surface detail */}
                              {isOpen && (
                                <div className="px-4 pb-3 pl-9 space-y-2 bg-surface-secondary/30">
                                  {account.surfaces.map((surface) => {
                                    const sb = surfaceBadge(surface, now);
                                    const progress = surfaceProgress(surface);
                                    const detail = failedDetail(surface);
                                    const count = itemCount(surface);
                                    return (
                                      <div key={surface.surface} className="pt-1">
                                        <div className="flex items-center justify-between gap-2 text-xs">
                                          <span className="flex items-center gap-2 min-w-0">
                                            <span className={`w-1.5 h-1.5 rounded-full ${TONE_DOT[sb.tone]}`} />
                                            <span className="text-content-secondary capitalize">
                                              {surface.surface}
                                            </span>
                                            {count !== null && (
                                              <span className="text-content-muted">· {count}</span>
                                            )}
                                          </span>
                                          <span className="flex items-center gap-2 shrink-0">
                                            {surface.state === "rate_limited" && (
                                              <span className="text-content-muted">
                                                retry {untilTime(surface.retry_at, now)}
                                              </span>
                                            )}
                                            <span className={TONE_TEXT[sb.tone]}>{sb.label}</span>
                                          </span>
                                        </div>
                                        <SyncProgressBar progress={progress} />
                                        {detail !== null && (
                                          <p className="text-[11px] text-red-400/70 line-clamp-2 mt-0.5 pl-3.5">
                                            {detail}
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })}
                                  <div className="flex items-center gap-2 pt-2 mt-1 border-t border-edge/40">
                                    {account.state.state === "auth_lost" && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setReauthLabel(account.display);
                                          setShowModal(true);
                                        }}
                                        className="px-2.5 py-1 rounded-md text-xs text-amber-400 hover:bg-amber-500/10 border border-amber-500/30 bg-transparent cursor-pointer"
                                      >
                                        {repairLabel(account.state.repair)}
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => { handleDisconnect(source.source_id, account); }}
                                      aria-label={`Disconnect ${account.display}`}
                                      title="Disconnect"
                                      className="px-2.5 py-1 rounded-md text-xs text-content-muted hover:text-red-400 hover:bg-red-500/10 border border-edge bg-transparent cursor-pointer"
                                    >
                                      Disconnect
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {showModal && (
        <AddAccountModal
          reauthLabel={reauthLabel}
          onClose={() => { setShowModal(false); setReauthLabel(undefined); }}
          onConnected={() => {
            setShowModal(false);
            setReauthLabel(undefined);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
